// Nationalities — one list for every nationality field: the website's form, the staff application fields, the applicant's
// own page, the app's booking form and the server's rule (leasing/applicant.schemas.ts). Three identical copies, because
// the server, the web app and the Expo app build separately:
//   src/modules/shared/nationalities.ts   (the source)
//   frontend/src/lib/nationalities.ts
//   mobile/src/lib/nationalities.ts
// src/modules/shared/nationalities.test.ts fails if they drift. Edit this one and copy it over the other two.
//
// The stored value is the nationality as a word ("British"), as Goodlord's identity.nationality and our forms already
// hold it, so the values stored before this list existed stay readable. The United Kingdom comes first, then the rest
// by country, A to Z. Plain ASCII names so every runtime sorts and searches them alike.

export interface Nationality {
  /** ISO 3166-1 alpha-2 (Kosovo uses the common XK). */
  code: string;
  country: string;
  /** What is stored, e.g. "British". */
  nationality: string;
}

export const NATIONALITIES: readonly Nationality[] = [
  { code: 'GB', country: 'United Kingdom', nationality: 'British' },
  { code: 'AF', country: 'Afghanistan', nationality: 'Afghan' },
  { code: 'AL', country: 'Albania', nationality: 'Albanian' },
  { code: 'DZ', country: 'Algeria', nationality: 'Algerian' },
  { code: 'AD', country: 'Andorra', nationality: 'Andorran' },
  { code: 'AO', country: 'Angola', nationality: 'Angolan' },
  { code: 'AG', country: 'Antigua and Barbuda', nationality: 'Citizen of Antigua and Barbuda' },
  { code: 'AR', country: 'Argentina', nationality: 'Argentine' },
  { code: 'AM', country: 'Armenia', nationality: 'Armenian' },
  { code: 'AU', country: 'Australia', nationality: 'Australian' },
  { code: 'AT', country: 'Austria', nationality: 'Austrian' },
  { code: 'AZ', country: 'Azerbaijan', nationality: 'Azerbaijani' },
  { code: 'BS', country: 'Bahamas', nationality: 'Bahamian' },
  { code: 'BH', country: 'Bahrain', nationality: 'Bahraini' },
  { code: 'BD', country: 'Bangladesh', nationality: 'Bangladeshi' },
  { code: 'BB', country: 'Barbados', nationality: 'Barbadian' },
  { code: 'BY', country: 'Belarus', nationality: 'Belarusian' },
  { code: 'BE', country: 'Belgium', nationality: 'Belgian' },
  { code: 'BZ', country: 'Belize', nationality: 'Belizean' },
  { code: 'BJ', country: 'Benin', nationality: 'Beninese' },
  { code: 'BT', country: 'Bhutan', nationality: 'Bhutanese' },
  { code: 'BO', country: 'Bolivia', nationality: 'Bolivian' },
  { code: 'BA', country: 'Bosnia and Herzegovina', nationality: 'Citizen of Bosnia and Herzegovina' },
  { code: 'BW', country: 'Botswana', nationality: 'Botswanan' },
  { code: 'BR', country: 'Brazil', nationality: 'Brazilian' },
  { code: 'BN', country: 'Brunei', nationality: 'Bruneian' },
  { code: 'BG', country: 'Bulgaria', nationality: 'Bulgarian' },
  { code: 'BF', country: 'Burkina Faso', nationality: 'Burkinan' },
  { code: 'BI', country: 'Burundi', nationality: 'Burundian' },
  { code: 'KH', country: 'Cambodia', nationality: 'Cambodian' },
  { code: 'CM', country: 'Cameroon', nationality: 'Cameroonian' },
  { code: 'CA', country: 'Canada', nationality: 'Canadian' },
  { code: 'CV', country: 'Cape Verde', nationality: 'Cape Verdean' },
  { code: 'CF', country: 'Central African Republic', nationality: 'Central African' },
  { code: 'TD', country: 'Chad', nationality: 'Chadian' },
  { code: 'CL', country: 'Chile', nationality: 'Chilean' },
  { code: 'CN', country: 'China', nationality: 'Chinese' },
  { code: 'CO', country: 'Colombia', nationality: 'Colombian' },
  { code: 'KM', country: 'Comoros', nationality: 'Comoran' },
  { code: 'CG', country: 'Congo', nationality: 'Congolese (Congo)' },
  { code: 'CD', country: 'Congo (Democratic Republic)', nationality: 'Congolese (DRC)' },
  { code: 'CR', country: 'Costa Rica', nationality: 'Costa Rican' },
  { code: 'CI', country: 'Cote d\'Ivoire', nationality: 'Ivorian' },
  { code: 'HR', country: 'Croatia', nationality: 'Croatian' },
  { code: 'CU', country: 'Cuba', nationality: 'Cuban' },
  { code: 'CY', country: 'Cyprus', nationality: 'Cypriot' },
  { code: 'CZ', country: 'Czechia', nationality: 'Czech' },
  { code: 'DK', country: 'Denmark', nationality: 'Danish' },
  { code: 'DJ', country: 'Djibouti', nationality: 'Djiboutian' },
  { code: 'DM', country: 'Dominica', nationality: 'Dominican' },
  { code: 'DO', country: 'Dominican Republic', nationality: 'Citizen of the Dominican Republic' },
  { code: 'TL', country: 'East Timor', nationality: 'East Timorese' },
  { code: 'EC', country: 'Ecuador', nationality: 'Ecuadorean' },
  { code: 'EG', country: 'Egypt', nationality: 'Egyptian' },
  { code: 'SV', country: 'El Salvador', nationality: 'Salvadorean' },
  { code: 'GQ', country: 'Equatorial Guinea', nationality: 'Equatorial Guinean' },
  { code: 'ER', country: 'Eritrea', nationality: 'Eritrean' },
  { code: 'EE', country: 'Estonia', nationality: 'Estonian' },
  { code: 'SZ', country: 'Eswatini', nationality: 'Swazi' },
  { code: 'ET', country: 'Ethiopia', nationality: 'Ethiopian' },
  { code: 'FJ', country: 'Fiji', nationality: 'Fijian' },
  { code: 'FI', country: 'Finland', nationality: 'Finnish' },
  { code: 'FR', country: 'France', nationality: 'French' },
  { code: 'GA', country: 'Gabon', nationality: 'Gabonese' },
  { code: 'GM', country: 'Gambia', nationality: 'Gambian' },
  { code: 'GE', country: 'Georgia', nationality: 'Georgian' },
  { code: 'DE', country: 'Germany', nationality: 'German' },
  { code: 'GH', country: 'Ghana', nationality: 'Ghanaian' },
  { code: 'GR', country: 'Greece', nationality: 'Greek' },
  { code: 'GD', country: 'Grenada', nationality: 'Grenadian' },
  { code: 'GT', country: 'Guatemala', nationality: 'Guatemalan' },
  { code: 'GN', country: 'Guinea', nationality: 'Guinean' },
  { code: 'GW', country: 'Guinea-Bissau', nationality: 'Citizen of Guinea-Bissau' },
  { code: 'GY', country: 'Guyana', nationality: 'Guyanese' },
  { code: 'HT', country: 'Haiti', nationality: 'Haitian' },
  { code: 'HN', country: 'Honduras', nationality: 'Honduran' },
  { code: 'HK', country: 'Hong Kong', nationality: 'Hong Konger' },
  { code: 'HU', country: 'Hungary', nationality: 'Hungarian' },
  { code: 'IS', country: 'Iceland', nationality: 'Icelandic' },
  { code: 'IN', country: 'India', nationality: 'Indian' },
  { code: 'ID', country: 'Indonesia', nationality: 'Indonesian' },
  { code: 'IR', country: 'Iran', nationality: 'Iranian' },
  { code: 'IQ', country: 'Iraq', nationality: 'Iraqi' },
  { code: 'IE', country: 'Ireland', nationality: 'Irish' },
  { code: 'IL', country: 'Israel', nationality: 'Israeli' },
  { code: 'IT', country: 'Italy', nationality: 'Italian' },
  { code: 'JM', country: 'Jamaica', nationality: 'Jamaican' },
  { code: 'JP', country: 'Japan', nationality: 'Japanese' },
  { code: 'JO', country: 'Jordan', nationality: 'Jordanian' },
  { code: 'KZ', country: 'Kazakhstan', nationality: 'Kazakh' },
  { code: 'KE', country: 'Kenya', nationality: 'Kenyan' },
  { code: 'KI', country: 'Kiribati', nationality: 'Citizen of Kiribati' },
  { code: 'XK', country: 'Kosovo', nationality: 'Kosovan' },
  { code: 'KW', country: 'Kuwait', nationality: 'Kuwaiti' },
  { code: 'KG', country: 'Kyrgyzstan', nationality: 'Kyrgyz' },
  { code: 'LA', country: 'Laos', nationality: 'Lao' },
  { code: 'LV', country: 'Latvia', nationality: 'Latvian' },
  { code: 'LB', country: 'Lebanon', nationality: 'Lebanese' },
  { code: 'LS', country: 'Lesotho', nationality: 'Mosotho' },
  { code: 'LR', country: 'Liberia', nationality: 'Liberian' },
  { code: 'LY', country: 'Libya', nationality: 'Libyan' },
  { code: 'LI', country: 'Liechtenstein', nationality: 'Liechtenstein citizen' },
  { code: 'LT', country: 'Lithuania', nationality: 'Lithuanian' },
  { code: 'LU', country: 'Luxembourg', nationality: 'Luxembourger' },
  { code: 'MO', country: 'Macao', nationality: 'Macanese' },
  { code: 'MG', country: 'Madagascar', nationality: 'Malagasy' },
  { code: 'MW', country: 'Malawi', nationality: 'Malawian' },
  { code: 'MY', country: 'Malaysia', nationality: 'Malaysian' },
  { code: 'MV', country: 'Maldives', nationality: 'Maldivian' },
  { code: 'ML', country: 'Mali', nationality: 'Malian' },
  { code: 'MT', country: 'Malta', nationality: 'Maltese' },
  { code: 'MH', country: 'Marshall Islands', nationality: 'Marshallese' },
  { code: 'MR', country: 'Mauritania', nationality: 'Mauritanian' },
  { code: 'MU', country: 'Mauritius', nationality: 'Mauritian' },
  { code: 'MX', country: 'Mexico', nationality: 'Mexican' },
  { code: 'FM', country: 'Micronesia', nationality: 'Micronesian' },
  { code: 'MD', country: 'Moldova', nationality: 'Moldovan' },
  { code: 'MC', country: 'Monaco', nationality: 'Monegasque' },
  { code: 'MN', country: 'Mongolia', nationality: 'Mongolian' },
  { code: 'ME', country: 'Montenegro', nationality: 'Montenegrin' },
  { code: 'MA', country: 'Morocco', nationality: 'Moroccan' },
  { code: 'MZ', country: 'Mozambique', nationality: 'Mozambican' },
  { code: 'MM', country: 'Myanmar', nationality: 'Burmese' },
  { code: 'NA', country: 'Namibia', nationality: 'Namibian' },
  { code: 'NR', country: 'Nauru', nationality: 'Nauruan' },
  { code: 'NP', country: 'Nepal', nationality: 'Nepalese' },
  { code: 'NL', country: 'Netherlands', nationality: 'Dutch' },
  { code: 'NZ', country: 'New Zealand', nationality: 'New Zealander' },
  { code: 'NI', country: 'Nicaragua', nationality: 'Nicaraguan' },
  { code: 'NE', country: 'Niger', nationality: 'Nigerien' },
  { code: 'NG', country: 'Nigeria', nationality: 'Nigerian' },
  { code: 'KP', country: 'North Korea', nationality: 'North Korean' },
  { code: 'MK', country: 'North Macedonia', nationality: 'Macedonian' },
  { code: 'NO', country: 'Norway', nationality: 'Norwegian' },
  { code: 'OM', country: 'Oman', nationality: 'Omani' },
  { code: 'PK', country: 'Pakistan', nationality: 'Pakistani' },
  { code: 'PW', country: 'Palau', nationality: 'Palauan' },
  { code: 'PS', country: 'Palestine', nationality: 'Palestinian' },
  { code: 'PA', country: 'Panama', nationality: 'Panamanian' },
  { code: 'PG', country: 'Papua New Guinea', nationality: 'Papua New Guinean' },
  { code: 'PY', country: 'Paraguay', nationality: 'Paraguayan' },
  { code: 'PE', country: 'Peru', nationality: 'Peruvian' },
  { code: 'PH', country: 'Philippines', nationality: 'Filipino' },
  { code: 'PL', country: 'Poland', nationality: 'Polish' },
  { code: 'PT', country: 'Portugal', nationality: 'Portuguese' },
  { code: 'QA', country: 'Qatar', nationality: 'Qatari' },
  { code: 'RO', country: 'Romania', nationality: 'Romanian' },
  { code: 'RU', country: 'Russia', nationality: 'Russian' },
  { code: 'RW', country: 'Rwanda', nationality: 'Rwandan' },
  { code: 'WS', country: 'Samoa', nationality: 'Samoan' },
  { code: 'SM', country: 'San Marino', nationality: 'Sammarinese' },
  { code: 'ST', country: 'Sao Tome and Principe', nationality: 'Sao Tomean' },
  { code: 'SA', country: 'Saudi Arabia', nationality: 'Saudi Arabian' },
  { code: 'SN', country: 'Senegal', nationality: 'Senegalese' },
  { code: 'RS', country: 'Serbia', nationality: 'Serbian' },
  { code: 'SC', country: 'Seychelles', nationality: 'Citizen of Seychelles' },
  { code: 'SL', country: 'Sierra Leone', nationality: 'Sierra Leonean' },
  { code: 'SG', country: 'Singapore', nationality: 'Singaporean' },
  { code: 'SK', country: 'Slovakia', nationality: 'Slovak' },
  { code: 'SI', country: 'Slovenia', nationality: 'Slovenian' },
  { code: 'SB', country: 'Solomon Islands', nationality: 'Solomon Islander' },
  { code: 'SO', country: 'Somalia', nationality: 'Somali' },
  { code: 'ZA', country: 'South Africa', nationality: 'South African' },
  { code: 'KR', country: 'South Korea', nationality: 'South Korean' },
  { code: 'SS', country: 'South Sudan', nationality: 'South Sudanese' },
  { code: 'ES', country: 'Spain', nationality: 'Spanish' },
  { code: 'LK', country: 'Sri Lanka', nationality: 'Sri Lankan' },
  { code: 'KN', country: 'St Kitts and Nevis', nationality: 'Kittitian' },
  { code: 'LC', country: 'St Lucia', nationality: 'St Lucian' },
  { code: 'VC', country: 'St Vincent and the Grenadines', nationality: 'Vincentian' },
  { code: 'SD', country: 'Sudan', nationality: 'Sudanese' },
  { code: 'SR', country: 'Suriname', nationality: 'Surinamese' },
  { code: 'SE', country: 'Sweden', nationality: 'Swedish' },
  { code: 'CH', country: 'Switzerland', nationality: 'Swiss' },
  { code: 'SY', country: 'Syria', nationality: 'Syrian' },
  { code: 'TW', country: 'Taiwan', nationality: 'Taiwanese' },
  { code: 'TJ', country: 'Tajikistan', nationality: 'Tajik' },
  { code: 'TZ', country: 'Tanzania', nationality: 'Tanzanian' },
  { code: 'TH', country: 'Thailand', nationality: 'Thai' },
  { code: 'TG', country: 'Togo', nationality: 'Togolese' },
  { code: 'TO', country: 'Tonga', nationality: 'Tongan' },
  { code: 'TT', country: 'Trinidad and Tobago', nationality: 'Trinidadian' },
  { code: 'TN', country: 'Tunisia', nationality: 'Tunisian' },
  { code: 'TR', country: 'Turkey', nationality: 'Turkish' },
  { code: 'TM', country: 'Turkmenistan', nationality: 'Turkmen' },
  { code: 'TV', country: 'Tuvalu', nationality: 'Tuvaluan' },
  { code: 'UG', country: 'Uganda', nationality: 'Ugandan' },
  { code: 'UA', country: 'Ukraine', nationality: 'Ukrainian' },
  { code: 'AE', country: 'United Arab Emirates', nationality: 'Emirati' },
  { code: 'US', country: 'United States', nationality: 'American' },
  { code: 'UY', country: 'Uruguay', nationality: 'Uruguayan' },
  { code: 'UZ', country: 'Uzbekistan', nationality: 'Uzbek' },
  { code: 'VU', country: 'Vanuatu', nationality: 'Citizen of Vanuatu' },
  { code: 'VA', country: 'Vatican City', nationality: 'Vatican citizen' },
  { code: 'VE', country: 'Venezuela', nationality: 'Venezuelan' },
  { code: 'VN', country: 'Vietnam', nationality: 'Vietnamese' },
  { code: 'YE', country: 'Yemen', nationality: 'Yemeni' },
  { code: 'ZM', country: 'Zambia', nationality: 'Zambian' },
  { code: 'ZW', country: 'Zimbabwe', nationality: 'Zimbabwean' },
];

const key = (v: string) => v.trim().toLowerCase();
const LOOKUP = new Map<string, Nationality>();
for (const n of NATIONALITIES) for (const k of [n.code, n.country, n.nationality]) if (!LOOKUP.has(key(k))) LOOKUP.set(key(k), n);

/** The list entry for a nationality, a country name or an ISO code, in any case ("british", "United Kingdom", "gb"), or null. */
export function findNationality(value: string | null | undefined): Nationality | null {
  return typeof value === 'string' && value.trim() ? LOOKUP.get(key(value)) ?? null : null;
}

/** Exactly one of the list's nationalities ("British", not "british" or "UK"). */
export function isNationality(value: string | null | undefined): boolean {
  return !!value && findNationality(value)?.nationality === value;
}

/** British and Irish citizens do not need a Right to Rent share code; everyone else does (England's Right to Rent check). */
export const SHARE_CODE_EXEMPT: readonly string[] = ['British', 'Irish'];

/** A nationality from the list that needs a Right to Rent share code. An empty or unknown value needs none (it is refused on its own). */
export function needsShareCode(nationality: string | null | undefined): boolean {
  const n = findNationality(nationality);
  return !!n && !SHARE_CODE_EXEMPT.includes(n.nationality);
}

/** Matches for a search box: by nationality or country, the ones that start with the text first. Empty text → the whole list. */
export function searchNationalities(query: string): Nationality[] {
  const q = key(query);
  if (!q) return [...NATIONALITIES];
  const starts: Nationality[] = [];
  const has: Nationality[] = [];
  for (const n of NATIONALITIES) {
    const a = n.nationality.toLowerCase();
    const c = n.country.toLowerCase();
    if (a.startsWith(q) || c.startsWith(q) || n.code.toLowerCase() === q) starts.push(n);
    else if (a.includes(q) || c.includes(q)) has.push(n);
  }
  return [...starts, ...has];
}
